import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchInvoiceById, publishInvoice, deleteInvoiceItem, type InvoiceStatus } from '../../services/invoiceApi';
import { fetchFeeDiscounts, type FeeDiscountRecord } from '../../services/feeDiscountApi';
import { getSchoolId } from '../../services/auth';
import './InvoiceDetail.css';

const STATUS_LABELS: Record<InvoiceStatus, string> = {
    paid: 'Paid', partial: 'Partial', overdue: 'Overdue', published: 'Published', draft: 'Draft', void: 'Void',
};

function formatCurrency(n: number): string {
    return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Internal working types (amounts as numbers for computation) ── */
type DetailLineItem = {
    id: string;
    fee_head_id: string;
    fee_head_name: string;
    description: string | null;
    gross_amount: number;
    discount_amount: number;
    net_amount: number;
    is_optional: boolean;
    applied_discount_id: string | null;
};

type DetailInvoice = {
    id: string;
    invoice_number: string;
    title: string;
    status: InvoiceStatus;
    total_amount: number;
    paid_amount: number;
    balance_amount: number;
    issue_date: string;
    due_date: string | null;
    student: { id: string; name: string | null; enrollment_number: string | null } | null;
    academic_session: { id: string; name: string };
    items: DetailLineItem[];
};

interface Props {
    invoiceId: string;
    onClose: (changed: boolean) => void;
}

let nextId = 100;

export default function InvoiceDetail({ invoiceId, onClose }: Props) {
    const [invoice, setInvoice] = useState<DetailInvoice | null>(null);
    const [discounts, setDiscounts] = useState<FeeDiscountRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [apiError, setApiError] = useState('');

    /* Track whether the invoice was mutated (saved/published) */
    const dirtyRef = useRef(false);
    const handleClose = () => onClose(dirtyRef.current);

    /* ── Modals ── */
    const [showAddFeeModal, setShowAddFeeModal] = useState(false);
    const [showPublishModal, setShowPublishModal] = useState(false);

    /* ── Add Fee form ── */
    const [_addFeeFeeHeadId, setAddFeeFeeHeadId] = useState('');
    const [addFeeFeeHeadName, setAddFeeFeeHeadName] = useState('');
    const [addFeeDesc, setAddFeeDesc] = useState('');
    const [addFeeAmount, setAddFeeAmount] = useState('');
    const [addFeeError, setAddFeeError] = useState('');

    /* ── Discount picker ── */
    const [discountPickerPolicy, setDiscountPickerPolicy] = useState<FeeDiscountRecord | null>(null);
    const [discountPickerLineId, setDiscountPickerLineId] = useState('');
    const [showManualWaiver, setShowManualWaiver] = useState(false);
    const [manualWaiverLineId, setManualWaiverLineId] = useState('');
    const [manualWaiverAmount, setManualWaiverAmount] = useState('');

    const isEditable = invoice?.status === 'draft';

    /* ── Load invoice ── */
    const loadInvoice = useCallback(async () => {
        setLoading(true);
        setApiError('');
        try {
            const data = await fetchInvoiceById(invoiceId);
            setInvoice({
                id: data.id,
                invoice_number: data.invoice_number,
                title: data.title,
                status: data.status,
                total_amount: parseFloat(data.total_amount),
                paid_amount: parseFloat(data.paid_amount),
                balance_amount: parseFloat(data.balance_amount),
                issue_date: data.issue_date,
                due_date: data.due_date,
                student: data.student,
                academic_session: data.academic_session,
                items: data.items.map((item) => ({
                    id: item.id,
                    fee_head_id: item.fee_head_id,
                    fee_head_name: item.fee_head.name,
                    description: item.description,
                    gross_amount: parseFloat(item.gross_amount),
                    discount_amount: parseFloat(item.discount_amount),
                    net_amount: parseFloat(item.net_amount),
                    is_optional: item.is_optional,
                    applied_discount_id: item.applied_discount_id,
                })),
            });
        } catch {
            setApiError('Failed to load invoice.');
        } finally {
            setLoading(false);
        }
    }, [invoiceId]);

    useEffect(() => { loadInvoice(); }, [loadInvoice]);

    /* ── Load discounts ── */
    useEffect(() => {
        fetchFeeDiscounts({ school_id: getSchoolId() ?? undefined })
            .then((data) => setDiscounts(data.filter((d) => d.is_active)))
            .catch(() => {});
    }, []);

    const showSuccessToast = (msg: string) => {
        setSuccessMsg(msg);
        setTimeout(() => setSuccessMsg(''), 5000);
    };

    /* ── Save draft ── */
    const handleSaveDraft = async () => {
        if (!invoice) return;
        setSaving(true);
        try {
            // TODO: wire to real PATCH /api/student-invoices/:id endpoint
            dirtyRef.current = true;
            showSuccessToast('Draft saved.');
        } catch {
            setApiError('Failed to save draft.');
        } finally {
            setSaving(false);
        }
    };

    /* ── Publish ── */
    const handlePublish = async () => {
        if (!invoice) return;
        setPublishing(true);
        try {
            await publishInvoice(invoice.id);
            setInvoice((prev) => prev ? { ...prev, status: 'published' } : prev);
            setShowPublishModal(false);
            dirtyRef.current = true;
            showSuccessToast('Invoice published.');
        } catch {
            setApiError('Failed to publish.');
        } finally {
            setPublishing(false);
        }
    };

    /* ── Add line item ── */
    const handleAddFee = () => {
        if (!invoice || !addFeeFeeHeadName.trim() || !addFeeAmount) return;
        const gross = parseFloat(addFeeAmount);
        if (isNaN(gross) || gross <= 0) return;
        const name = addFeeFeeHeadName.trim();
        if (invoice.items.some((li) => li.fee_head_name.trim().toLowerCase() === name.toLowerCase())) {
            setAddFeeError('This fee head is already on the invoice — duplicate fee heads are not allowed.');
            return;
        }
        const newItem: DetailLineItem = {
            id: `li-new-${nextId++}`,
            fee_head_id: '',
            fee_head_name: addFeeFeeHeadName.trim(),
            description: addFeeDesc || null,
            is_optional: false,
            gross_amount: gross,
            discount_amount: 0,
            net_amount: gross,
            applied_discount_id: null,
        };
        setInvoice({
            ...invoice,
            items: [...invoice.items, newItem],
            total_amount: invoice.items.reduce((s, li) => s + li.net_amount, 0) + newItem.net_amount,
            balance_amount: invoice.items.reduce((s, li) => s + li.net_amount, 0) + newItem.net_amount - invoice.paid_amount,
        });
        setShowAddFeeModal(false);
        setAddFeeFeeHeadId('');
        setAddFeeFeeHeadName('');
        setAddFeeDesc('');
        setAddFeeAmount('');
        setAddFeeError('');
    };

    /* ── Apply discount from policy card ── */
    const handleApplyPolicy = () => {
        if (!invoice || !discountPickerPolicy || !discountPickerLineId) return;
        const lineIdx = invoice.items.findIndex((li) => li.id === discountPickerLineId);
        if (lineIdx < 0) return;
        const li = invoice.items[lineIdx];
        const val = Number(discountPickerPolicy.value);
        const discountAmt = discountPickerPolicy.calculation_type === 'percentage'
            ? Math.round(li.gross_amount * val / 100)
            : val;
        applyDiscountToLine(lineIdx, discountAmt);
        setDiscountPickerPolicy(null);
        setDiscountPickerLineId('');
    };

    /* ── Apply manual waiver ── */
    const handleApplyManualWaiver = () => {
        if (!invoice || !manualWaiverLineId || !manualWaiverAmount) return;
        const amt = parseFloat(manualWaiverAmount);
        if (isNaN(amt) || amt <= 0) return;
        const lineIdx = invoice.items.findIndex((li) => li.id === manualWaiverLineId);
        if (lineIdx < 0) return;
        applyDiscountToLine(lineIdx, amt);
        setShowManualWaiver(false);
        setManualWaiverLineId('');
        setManualWaiverAmount('');
    };

    /* ── Shared helper: apply discount to a line ── */
    const applyDiscountToLine = (lineIdx: number, discountAmt: number) => {
        if (!invoice) return;
        const updatedItems = [...invoice.items];
        const li = { ...updatedItems[lineIdx] };
        li.discount_amount = discountAmt;
        li.net_amount = Math.max(0, li.gross_amount - li.discount_amount);
        updatedItems[lineIdx] = li;
        const total = updatedItems.reduce((s, item) => s + item.net_amount, 0);
        setInvoice({ ...invoice, items: updatedItems, total_amount: total, balance_amount: total - invoice.paid_amount });
    };

    /* ── Remove discount from a line ── */
    const handleRemoveDiscount = (lineId: string) => {
        if (!invoice) return;
        const lineIdx = invoice.items.findIndex((li) => li.id === lineId);
        if (lineIdx < 0) return;
        applyDiscountToLine(lineIdx, 0);
    };

    /* ── Delete line ── */
    const handleDeleteLine = async (lineId: string) => {
        if (!invoice) return;
        try {
            await deleteInvoiceItem(lineId);
            const updatedItems = invoice.items.filter((li) => li.id !== lineId);
            const total = updatedItems.reduce((s, item) => s + item.net_amount, 0);
            setInvoice({ ...invoice, items: updatedItems, total_amount: total, balance_amount: total - invoice.paid_amount });
            dirtyRef.current = true;
        } catch {
            setApiError('Failed to remove item.');
        }
    };

    /* ── Computed ── */
    const subtotal = invoice?.items.reduce((s, li) => s + li.gross_amount, 0) ?? 0;
    const totalDiscount = invoice?.items.reduce((s, li) => s + li.discount_amount, 0) ?? 0;
    const netPayable = subtotal - totalDiscount;

    /* ══════════════════════════════════════════════════════════════════ */
    /*  Render — everything wrapped in backdrop + aside (drawer)        */
    /* ══════════════════════════════════════════════════════════════════ */

    /* ── Loading skeleton ── */
    if (loading) {
        return (
            <>
                <div className="inv-detail__backdrop" onClick={handleClose} />
                <aside className="inv-detail" role="dialog" aria-label="Loading invoice">
                    <header className="inv-detail__header">
                        <div className="inv-detail__header-left">
                            <button className="inv-detail__close-btn" onClick={handleClose} aria-label="Close panel">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                    </header>
                    <div className="inv-detail__body">
                        <div className="inv-detail__loading">
                            <div className="inv-detail__loader"><span className="inv-detail__loader-dot" /><span className="inv-detail__loader-dot" /><span className="inv-detail__loader-dot" /></div>
                        </div>
                    </div>
                </aside>
            </>
        );
    }

    /* ── Not found ── */
    if (!invoice) {
        return (
            <>
                <div className="inv-detail__backdrop" onClick={handleClose} />
                <aside className="inv-detail" role="dialog" aria-label="Invoice not found">
                    <header className="inv-detail__header">
                        <div className="inv-detail__header-left">
                            <button className="inv-detail__close-btn" onClick={handleClose} aria-label="Close panel">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>
                    </header>
                    <div className="inv-detail__body">
                        <div className="inv-detail__empty-state">
                            <p>Invoice not found.</p>
                        </div>
                    </div>
                </aside>
            </>
        );
    }

    return (
        <>
            <div className="inv-detail__backdrop" onClick={handleClose} />

            <aside className="inv-detail" role="dialog" aria-label={`Invoice ${invoice.invoice_number}`}>
                {/* ── Header ── */}
                <header className="inv-detail__header">
                    <div className="inv-detail__header-left">
                        <button className="inv-detail__close-btn" onClick={handleClose} aria-label="Close panel" title="Close">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                        <div>
                            <div className="inv-detail__title-row">
                                <h2 className="inv-detail__title">{invoice.invoice_number}</h2>
                                <span className={`inv-detail__badge inv-detail__badge--${invoice.status}`}>{STATUS_LABELS[invoice.status]}</span>
                            </div>
                            <p className="inv-detail__subtitle">{invoice.student?.name ?? invoice.student?.enrollment_number ?? 'Unknown student'} · {invoice.academic_session.name}</p>
                        </div>
                    </div>
                    <div className="inv-detail__header-right">
                        {isEditable && (
                            <>
                                <button className="inv-detail__btn inv-detail__btn--secondary" onClick={handleSaveDraft} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save Draft'}
                                </button>
                                <button className="inv-detail__btn inv-detail__btn--primary" onClick={() => setShowPublishModal(true)} disabled={saving}>
                                    Publish
                                </button>
                            </>
                        )}
                    </div>
                </header>

                {/* ── Scrollable Body ── */}
                <div className="inv-detail__body">
                    {/* Feedback */}
                    {apiError && (
                        <div className="inv-detail__error-banner" role="alert">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" /><line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /><circle cx="12" cy="16" r="1" fill="currentColor" /></svg>
                            <span>{apiError}</span>
                            <button className="inv-detail__error-dismiss" onClick={() => setApiError('')}>×</button>
                        </div>
                    )}
                    {successMsg && (
                        <div className="inv-detail__toast">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                            <span>{successMsg}</span>
                            <button className="inv-detail__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                        </div>
                    )}

                    {/* ── Line Items Table ── */}
                    <div className="inv-detail__table-wrap">
                        <table className="inv-detail__table">
                            <thead>
                                <tr>
                                    <th className="inv-detail__th">Fee Head / Description</th>
                                    <th className="inv-detail__th inv-detail__th--amount">Gross Amount</th>
                                    <th className="inv-detail__th inv-detail__th--amount">Discount</th>
                                    <th className="inv-detail__th inv-detail__th--amount">Net Amount</th>
                                    {isEditable && <th className="inv-detail__th inv-detail__th--actions"></th>}
                                </tr>
                            </thead>
                            <tbody>
                                {invoice.items.length === 0 && (
                                    <tr><td colSpan={isEditable ? 5 : 4} className="inv-detail__empty-cell">
                                        <p>No line items. Use "+ Add Fee Item" below.</p>
                                    </td></tr>
                                )}
                                {invoice.items.map((li) => (
                                    <tr className="inv-detail__row" key={li.id}>
                                        <td className="inv-detail__td">
                                            <span className="inv-detail__fee-name">{li.fee_head_name}</span>
                                            {li.description && <span className="inv-detail__fee-desc">{li.description}</span>}
                                        </td>
                                        <td className="inv-detail__td inv-detail__td--amount">{formatCurrency(li.gross_amount)}</td>
                                        <td className="inv-detail__td inv-detail__td--amount inv-detail__td--discount">
                                            {li.discount_amount > 0 ? (
                                                <span className="inv-detail__discount-tag">
                                                    −{formatCurrency(li.discount_amount)}
                                                    {isEditable && (
                                                        <button className="inv-detail__discount-remove" onClick={() => handleRemoveDiscount(li.id)} title="Remove discount">×</button>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="inv-detail__no-discount">—</span>
                                            )}
                                        </td>
                                        <td className="inv-detail__td inv-detail__td--amount inv-detail__td--net">{formatCurrency(li.net_amount)}</td>
                                        {isEditable && (
                                            <td className="inv-detail__td inv-detail__td--actions">
                                                <button className="inv-detail__delete-btn" onClick={() => handleDeleteLine(li.id)} title="Remove line item">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Add Fee Button ── */}
                    {isEditable && (
                        <div className="inv-detail__controls">
                            <button className="inv-detail__control-btn" onClick={() => { setAddFeeError(''); setShowAddFeeModal(true); }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                                Add Fee Item
                            </button>
                        </div>
                    )}

                    {/* ── Discounts Section ── */}
                    {isEditable && invoice.items.length > 0 && (
                        <div className="inv-detail__discounts-section">
                            <h4 className="inv-detail__discounts-title">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></svg>
                                Available Discounts
                            </h4>

                            <div className="inv-detail__discount-cards">
                                {discounts.length === 0 && (
                                    <p className="inv-detail__no-discounts">No active discounts configured.</p>
                                )}
                                {discounts.map((dp) => (
                                    <button
                                        key={dp.id}
                                        className={`inv-detail__discount-card ${discountPickerPolicy?.id === dp.id ? 'inv-detail__discount-card--selected' : ''}`}
                                        onClick={() => {
                                            setDiscountPickerPolicy(discountPickerPolicy?.id === dp.id ? null : dp);
                                            setDiscountPickerLineId(invoice.items[0]?.id ?? '');
                                        }}
                                    >
                                        <span className="inv-detail__dc-value">
                                            {dp.calculation_type === 'percentage' ? `${Number(dp.value)}%` : `₹${Number(dp.value)}`}
                                        </span>
                                        <span className="inv-detail__dc-name">{dp.name}</span>
                                        <span className="inv-detail__dc-type">{dp.calculation_type === 'percentage' ? 'Percentage' : 'Fixed'}</span>
                                    </button>
                                ))}
                            </div>

                            {/* ── Policy → Line picker ── */}
                            {discountPickerPolicy && (
                                <div className="inv-detail__discount-apply-bar">
                                    <span className="inv-detail__dab-label">
                                        Apply <strong>{discountPickerPolicy.name}</strong> to:
                                    </span>
                                    <select
                                        className="inv-detail__dab-select"
                                        value={discountPickerLineId}
                                        onChange={(e) => setDiscountPickerLineId(e.target.value)}
                                    >
                                        {invoice.items.map((li) => (
                                            <option key={li.id} value={li.id}>
                                                {li.fee_head_name} ({formatCurrency(li.gross_amount)})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        className="inv-detail__dab-apply"
                                        onClick={handleApplyPolicy}
                                        disabled={!discountPickerLineId}
                                    >Apply</button>
                                    <button
                                        className="inv-detail__dab-cancel"
                                        onClick={() => { setDiscountPickerPolicy(null); setDiscountPickerLineId(''); }}
                                    >Cancel</button>
                                </div>
                            )}

                            {/* ── Manual Waiver toggle ── */}
                            <div className="inv-detail__manual-waiver">
                                <button
                                    className="inv-detail__manual-waiver-toggle"
                                    onClick={() => { setShowManualWaiver(!showManualWaiver); setManualWaiverLineId(invoice.items[0]?.id ?? ''); }}
                                >
                                    {showManualWaiver ? '▾' : '▸'} Manual waiver
                                </button>
                                {showManualWaiver && (
                                    <div className="inv-detail__manual-waiver-form">
                                        <select
                                            className="inv-detail__dab-select"
                                            value={manualWaiverLineId}
                                            onChange={(e) => setManualWaiverLineId(e.target.value)}
                                        >
                                            {invoice.items.map((li) => (
                                                <option key={li.id} value={li.id}>
                                                    {li.fee_head_name} ({formatCurrency(li.gross_amount)})
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            className="inv-detail__manual-waiver-input"
                                            type="number"
                                            min="1"
                                            placeholder="Amount (₹)"
                                            value={manualWaiverAmount}
                                            onChange={(e) => setManualWaiverAmount(e.target.value)}
                                        />
                                        <button
                                            className="inv-detail__dab-apply"
                                            onClick={handleApplyManualWaiver}
                                            disabled={!manualWaiverLineId || !manualWaiverAmount}
                                        >Apply</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Summary Panel ── */}
                    <div className="inv-detail__summary">
                        <div className="inv-detail__summary-row">
                            <span className="inv-detail__summary-label">Subtotal</span>
                            <span className="inv-detail__summary-value">{formatCurrency(subtotal)}</span>
                        </div>
                        <div className="inv-detail__summary-row inv-detail__summary-row--discount">
                            <span className="inv-detail__summary-label">Total Discounts</span>
                            <span className="inv-detail__summary-value inv-detail__summary-value--discount">
                                {totalDiscount > 0 ? `−${formatCurrency(totalDiscount)}` : '—'}
                            </span>
                        </div>
                        <div className="inv-detail__summary-row inv-detail__summary-row--total">
                            <span className="inv-detail__summary-label">Net Payable</span>
                            <span className="inv-detail__summary-value inv-detail__summary-value--total">{formatCurrency(netPayable)}</span>
                        </div>
                    </div>
                </div>

                {/* ═══ MODALS (inside the drawer) ═══ */}

                {/* ── Add Fee Modal ── */}
                {showAddFeeModal && (
                    <>
                        <div className="inv-detail__overlay" onClick={() => setShowAddFeeModal(false)} />
                        <div className="inv-detail__modal" role="dialog">
                            <h3 className="inv-detail__modal-title">Add Fee Item</h3>
                            <div className="inv-detail__modal-field">
                                <label className="inv-detail__modal-label">Fee Head</label>
                                <input className="inv-detail__modal-input" type="text" placeholder="e.g. Tuition Fee" value={addFeeFeeHeadName} onChange={(e) => { setAddFeeFeeHeadName(e.target.value); if (addFeeError) setAddFeeError(''); }} />
                            </div>
                            <div className="inv-detail__modal-field">
                                <label className="inv-detail__modal-label">Description (optional)</label>
                                <input className="inv-detail__modal-input" type="text" placeholder="e.g. Annual 2025-26" value={addFeeDesc} onChange={(e) => setAddFeeDesc(e.target.value)} />
                            </div>
                            <div className="inv-detail__modal-field">
                                <label className="inv-detail__modal-label">Gross Amount (₹)</label>
                                <input className="inv-detail__modal-input" type="number" min="1" placeholder="0.00" value={addFeeAmount} onChange={(e) => setAddFeeAmount(e.target.value)} />
                            </div>
                            {addFeeError && <p className="inv-detail__modal-error">{addFeeError}</p>}
                            <div className="inv-detail__modal-actions">
                                <button className="inv-detail__modal-btn inv-detail__modal-btn--cancel" onClick={() => setShowAddFeeModal(false)}>Cancel</button>
                                <button className="inv-detail__modal-btn inv-detail__modal-btn--confirm" onClick={handleAddFee} disabled={!addFeeFeeHeadName.trim() || !addFeeAmount}>Add Item</button>
                            </div>
                        </div>
                    </>
                )}



                {/* ── Publish Confirmation Modal ── */}
                {showPublishModal && (
                    <>
                        <div className="inv-detail__overlay" onClick={() => !publishing && setShowPublishModal(false)} />
                        <div className="inv-detail__modal" role="dialog">
                            <h3 className="inv-detail__modal-title">Publish Invoice</h3>
                            <p className="inv-detail__modal-text">
                                Publishing <strong>{invoice.invoice_number}</strong> will make it visible to the student. This cannot be easily undone.
                            </p>
                            <div className="inv-detail__modal-actions">
                                <button className="inv-detail__modal-btn inv-detail__modal-btn--cancel" onClick={() => setShowPublishModal(false)} disabled={publishing}>Cancel</button>
                                <button className="inv-detail__modal-btn inv-detail__modal-btn--confirm" onClick={handlePublish} disabled={publishing}>
                                    {publishing ? 'Publishing…' : 'Publish'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </aside>
        </>
    );
}
